import { useEffect, useRef, type RefObject } from "react";

import {
  isInsideComposerFloatingLayer,
  isInsideRestingComposerControlScope,
} from "./composerEventScope";
import { selectionHoldsComposerOpen } from "./composerSelectionHold";

const COMPOSER_FORM_SELECTOR = '[data-chat-composer-form="true"]';
const COMPOSER_EDITOR_SELECTOR = '[data-testid="composer-editor"]';
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="menuitem"]';

function composerFormFor(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element ? target.closest<HTMLElement>(COMPOSER_FORM_SELECTOR) : null;
}

export interface ComposerFocusHandlers {
  /**
   * Focus arrived. `liftScrollCollapse` is false for the focus a browser
   * re-fires on the element that already held it when the window comes back.
   */
  readonly onFocused: (input: { readonly liftScrollCollapse: boolean }) => void;
  readonly onBlurred: () => void;
  /** The person edited the prompt, which outranks anything the layout wanted. */
  readonly onEditorActivity: () => void;
  /**
   * A press landed on the Composer itself rather than on one of its controls.
   * `restingPadding` is a press on the resting row outside the editor, which
   * is the only one that has to ask for focus: everywhere else the browser has
   * already put the caret where the person pointed.
   */
  readonly onSurfacePressed: (input: { readonly restingPadding: boolean }) => void;
}

/**
 * Whether the Composer holds focus, as the Resting Composer needs to know it.
 *
 * The listeners sit on the document rather than on the form so that the answer
 * does not depend on when the form element arrives. The Composer's form is
 * rendered by `ComposerSurface`, which a test may stand in for, and a ref that
 * has not been attached yet would silently register nothing at all.
 *
 * `focusin` and `focusout` rather than `focus` and `blur`: the events that
 * matter arrive on the editor, a picker trigger or the send button, and only
 * the bubbling pair reaches the form from a descendant.
 *
 * Losing focus is answered on the next frame, not at once. The browser reports
 * a blur before it reports where focus went, and a drag-select through the
 * timeline blurs the editor while the person is still working inside the
 * thread. Deciding a frame later lets both settle.
 */
export function useComposerFocusTracking(
  input: ComposerFocusHandlers & {
    readonly getTimelineScrollNode: () => HTMLElement | null;
    /** Read at event time so a press can tell the two layouts apart. */
    readonly isRestingRef: RefObject<boolean>;
  },
): void {
  const { onFocused, onBlurred, onEditorActivity, onSurfacePressed } = input;
  const { getTimelineScrollNode, isRestingRef } = input;
  const windowRefocusRef = useRef(false);
  const blurFrameRef = useRef<number | null>(null);
  const heldBySelectionRef = useRef(false);

  useEffect(() => {
    const cancelBlurCheck = () => {
      if (blurFrameRef.current === null) return;
      window.cancelAnimationFrame(blurFrameRef.current);
      blurFrameRef.current = null;
    };

    const runBlurCheck = () => {
      blurFrameRef.current = null;
      const active = document.activeElement;
      if (composerFormFor(active) || isInsideComposerFloatingLayer(active)) return;
      if (isInsideRestingComposerControlScope(active)) return;
      if (selectionHoldsComposerOpen(window.getSelection(), getTimelineScrollNode())) {
        // The check runs again from `selectionchange` once the selection goes.
        heldBySelectionRef.current = true;
        return;
      }
      heldBySelectionRef.current = false;
      onBlurred();
    };

    const scheduleBlurCheck = () => {
      cancelBlurCheck();
      blurFrameRef.current = window.requestAnimationFrame(runBlurCheck);
    };

    // Returning to the window re-fires focus on the element that already held
    // it. That focus arrives after the window's own event, so a window focus
    // marks the frame in which it should not lift a scroll collapse.
    let refocusFrame: number | null = null;
    const handleWindowFocus = () => {
      windowRefocusRef.current = true;
      if (refocusFrame !== null) window.cancelAnimationFrame(refocusFrame);
      refocusFrame = window.requestAnimationFrame(() => {
        refocusFrame = null;
        windowRefocusRef.current = false;
      });
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!composerFormFor(event.target)) return;
      // The relocated controls sit in the strip, which is inside the form.
      // Reaching for the model picker while the Composer rests would otherwise
      // expand it and move that picker out from under the pointer.
      if (isInsideRestingComposerControlScope(event.target)) return;
      cancelBlurCheck();
      onFocused({ liftScrollCollapse: !windowRefocusRef.current });
    };

    const handleFocusOut = (event: FocusEvent) => {
      const form = composerFormFor(event.target);
      if (!form) return;
      const next = event.relatedTarget;
      // Focus moving between the editor, a picker and the send button never
      // leaves the Composer. Neither does focus landing in one of its popups,
      // which the browser renders outside the form.
      if (next instanceof Node && form.contains(next)) return;
      if (isInsideComposerFloatingLayer(next)) return;
      scheduleBlurCheck();
    };

    const handleSelectionChange = () => {
      if (!heldBySelectionRef.current) return;
      if (selectionHoldsComposerOpen(window.getSelection(), getTimelineScrollNode())) return;
      heldBySelectionRef.current = false;
      scheduleBlurCheck();
    };

    const handleInput = (event: Event) => {
      if (!composerFormFor(event.target)) return;
      onEditorActivity();
    };

    const handlePointerDown = (event: PointerEvent) => {
      const form = composerFormFor(event.target);
      if (!form) return;
      if (isInsideRestingComposerControlScope(event.target)) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;
      const restingPadding =
        isRestingRef.current && !event.target.closest(COMPOSER_EDITOR_SELECTOR);
      if (restingPadding) {
        // Pressing the resting row's padding would otherwise blur the editor
        // that still holds focus: the Composer expands, the blur check runs,
        // and it rests again. Treating that padding like the editor keeps the
        // press from taking focus away, without stealing caret placement from
        // a press on text.
        event.preventDefault();
      }
      onSurfacePressed({ restingPadding });
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    document.addEventListener("selectionchange", handleSelectionChange);
    document.addEventListener("input", handleInput, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("input", handleInput, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      if (refocusFrame !== null) window.cancelAnimationFrame(refocusFrame);
      cancelBlurCheck();
      windowRefocusRef.current = false;
      heldBySelectionRef.current = false;
    };
  }, [
    getTimelineScrollNode,
    isRestingRef,
    onBlurred,
    onEditorActivity,
    onFocused,
    onSurfacePressed,
  ]);
}
